import axios from "axios";
import { API } from "./catalogueApi";

// withCredentials so the HttpOnly guest-cart cookie (and customer session) travel
// with every cart/checkout call. Browser sends Origin automatically -> CSRF ok.
const shop = axios.create({ baseURL: API, withCredentials: true });

// T21-FIX2: every state-changing customer call carries the customer CSRF token (readable rst_customer_csrf cookie)
shop.interceptors.request.use((cfg) => {
  if (!["get", "head", "options"].includes((cfg.method || "get").toLowerCase())) {
    const m = document.cookie.match(/(?:^|; )rst_customer_csrf=([^;]*)/);
    if (m) cfg.headers = { ...(cfg.headers || {}), "X-CSRF-Token": decodeURIComponent(m[1]) };
  }
  return cfg;
});

export const money = (cents) =>
  cents == null
    ? "-"
    : "$" + (cents / 100).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const notify = () => {
  try {
    window.dispatchEvent(new Event("cart-updated"));
    localStorage.setItem("rst_cart_ping", String(Date.now()));  // T52: other tabs refresh on the storage event
  } catch {
    /* no-op outside browser */
  }
};

// T51c: a 4xx carrying a user-safe message (e.g. 422 "Only 28 of … available") is shown as-is; 5xx / network -> the generic fallback
export const serverMsg = (e, fallback) => {
  const s = e?.response?.status, d = e?.response?.data?.detail;
  return s >= 400 && s < 500 && typeof d === "string" && d.trim() ? d : fallback;
};
export const capText = (max, reserved) => `Only ${max} available${reserved ? " (the rest is reserved for an enquiry in progress)" : ""}`;
export const getCart = () => shop.get("/cart").then((r) => r.data);
export const getCheckoutAssurances = () => shop.get("/checkout-assurances").then((r) => r.data);
// T52: opens the mini cart drawer (header icon, or after any successful Add to cart)
export const openCartDrawer = (detail = {}) => window.dispatchEvent(new CustomEvent("cart-drawer-open", { detail }));
export const addToCart = (product_id, quantity = 1, { silent = false } = {}) =>
  shop.post("/cart/lines", { product_id, quantity }).then((r) => {
    notify();
    if (!silent) openCartDrawer({ added: product_id });
    return r.data;
  });
export const setQty = (line_id, quantity) =>
  shop.patch(`/cart/lines/${line_id}`, { quantity }).then((r) => {
    notify();
    return r.data;
  });
export const removeLine = (line_id) =>
  shop.delete(`/cart/lines/${line_id}`).then((r) => {
    notify();
    return r.data;
  });

export const createCheckout = (address, idempotency_key, business, new_order) =>
  shop.post("/checkout", { address, idempotency_key, business, new_order }).then((r) => r.data);
export const dismissCartNotices = () => shop.post("/cart/notices/dismiss").then((r) => r.data);
export const dismissPendingOrder = () => shop.post("/cart/pending-order/dismiss").then((r) => r.data);
export const getCheckout = (sid) => shop.get(`/checkout/${sid}`).then((r) => r.data);
export const runValidate = (sid) => shop.post(`/checkout/${sid}/validate`).then((r) => r.data);
export const confirmReconfirmation = (sid) =>
  shop.post(`/checkout/${sid}/confirm-reconfirmation`).then((r) => r.data);
export const proceedToPayment = (sid) => shop.post(`/checkout/${sid}/proceed-to-payment`).then((r) => r.data);
export const placeOrder = (checkout_session_id, idempotency_key, payment_method) =>
  shop.post(`/orders`, { checkout_session_id, idempotency_key, payment_method }).then((r) => r.data);
export const getPaymentMethods = (sid) => shop.get(`/checkout/${sid}/payment-methods`).then((r) => r.data);
export const payOrder = (reference) => shop.post(`/orders/${reference}/pay`).then((r) => r.data);
export const getOrder = (reference) => shop.get(`/orders/${reference}`).then((r) => r.data);
export const requestOrderCancellation = (reference, reason) =>
  shop.post(`/orders/${reference}/request-cancellation`, reason ? { reason } : {}).then((r) => r.data);
export const orderInvoiceUrl = (reference) => `${API}/orders/${reference}/invoice`;
export const acceptFreightQuote = (token) =>
  shop.get(`/checkout/freight-quote/accept`, { params: { token } }).then((r) => r.data);
export const getFreightQuoteContext = () =>
  shop.get(`/checkout/freight-quote/context`).then((r) => r.data);
export const acceptFreightQuoteConfirm = () =>
  shop.post(`/checkout/freight-quote/accept`, {}).then((r) => r.data);
export const declineFreightQuote = () =>
  shop.post(`/checkout/freight-quote/decline`, {}).then((r) => r.data);

// Phase 8 (8F) order history + reorder ("Buy again").
export const getMyOrders = () => shop.get(`/orders`).then((r) => r.data);
export const reorderPreview = (reference, idempotency_key) =>
  shop.post(`/orders/${reference}/reorder`, {}, idempotency_key ? { headers: { "Idempotency-Key": idempotency_key } } : {}).then((r) => r.data);
export const reorderConfirm = (reference, product_ids) =>
  shop.post(`/orders/${reference}/reorder/confirm`, { product_ids }).then((r) => r.data);

// Phase 8 (8D) saved lists + (8G) save-as-list.
export const getLists = () => shop.get(`/lists`).then((r) => r.data);
export const createList = (name, description) => shop.post(`/lists`, { name, description }).then((r) => r.data);
export const saveCartAsList = (name) => shop.post(`/lists/from-cart`, { name }).then((r) => r.data);
export const renameList = (lid, name, description) => shop.patch(`/lists/${lid}`, { name, description }).then((r) => r.data);
export const deleteList = (lid) => shop.delete(`/lists/${lid}`).then((r) => r.data);
export const addListItem = (lid, product_id, quantity, note) => shop.post(`/lists/${lid}/items`, { product_id, quantity, note }).then((r) => r.data);
export const removeListItem = (lid, item_id) => shop.delete(`/lists/${lid}/items/${item_id}`).then((r) => r.data);
export const duplicateList = (lid) => shop.post(`/lists/${lid}/duplicate`, {}).then((r) => r.data);
export const shareList = (lid) => shop.post(`/lists/${lid}/share`, {}).then((r) => r.data);
export const listToCart = (lid) => shop.post(`/lists/${lid}/add-to-cart`, {}).then((r) => { notify(); return r.data; });
export const getSharedList = (token) => shop.get(`/lists/shared/${token}`).then((r) => r.data);

export default shop;
